import { useEffect } from "react";
import { useOrderManagementStore } from "../store/OrderManagementStore";

function getOrderTableByEventTarget(target: EventTarget | null) {
    if (target instanceof Element) {
        return target.closest<HTMLTableElement>(".order-table");
    }

    return null;
}

function getOrderTableList() {
    return Array.from(document.querySelectorAll<HTMLTableElement>(".order-table"));
}

function isSelectionInOrderTable(selection: Selection) {
    const orderTableList = getOrderTableList();

    if (!orderTableList.length) {
        return false;
    }

    return orderTableList.some((orderTableEl) => {
        return (
            (!!selection.anchorNode && orderTableEl.contains(selection.anchorNode)) ||
            (!!selection.focusNode && orderTableEl.contains(selection.focusNode))
        );
    });
}

function clearOrderTableSelection() {
    const selection = window.getSelection?.();

    if (!selection || selection.isCollapsed || !isSelectionInOrderTable(selection)) {
        return;
    }

    selection.removeAllRanges();
}

const useShiftClickOrderCheckbox = () => {
    const orderManagementStore = useOrderManagementStore();

    /** 虚拟表格与正常表格之间的切换，需要重新进行事件绑定，旧表格DOM已被component diff销毁 */
    const isOver1000Orders = orderManagementStore.table.orders?.length > 1000;

    useEffect(() => {
        const orderTableUserSelectMap = new Map<HTMLTableElement, string>();

        const disableOrderTableTextSelect = () => {
            getOrderTableList().forEach((orderTableEl) => {
                if (!orderTableUserSelectMap.has(orderTableEl)) {
                    orderTableUserSelectMap.set(orderTableEl, orderTableEl.style.userSelect);
                }

                orderTableEl.style.userSelect = "none";
            });
        };

        const restoreOrderTableTextSelect = () => {
            orderTableUserSelectMap.forEach((userSelect, orderTableEl) => {
                orderTableEl.style.userSelect = userSelect;
            });

            orderTableUserSelectMap.clear();
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Shift") {
                orderManagementStore.table.updateShiftPressed(true);
                disableOrderTableTextSelect();
                clearOrderTableSelection();
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === "Shift") {
                orderManagementStore.table.updateShiftPressed(false);
                clearOrderTableSelection();
                restoreOrderTableTextSelect();
            }
        };

        /** 避免由于窗口失焦而导致一直处于Shift状态 */
        const handleBlur = () => {
            orderManagementStore.table.updateShiftPressed(false);
            clearOrderTableSelection();
            restoreOrderTableTextSelect();
        };

        const handleSelectStart = (event: Event) => {
            const orderTableEl = getOrderTableByEventTarget(event.target);

            if (orderTableEl && (orderManagementStore.table.shiftPressed || (event as MouseEvent).shiftKey)) {
                event.preventDefault(); // 阻止文字选择
                clearOrderTableSelection();
            }
        };

        const handleMouseDown = (event: MouseEvent) => {
            if (!event.shiftKey || !getOrderTableByEventTarget(event.target)) {
                return;
            }

            disableOrderTableTextSelect();
            clearOrderTableSelection();
        };

        const handleShiftClickEnd = (event: MouseEvent) => {
            if (!(event.shiftKey || orderManagementStore.table.shiftPressed)) {
                return;
            }

            if (getOrderTableByEventTarget(event.target)) {
                clearOrderTableSelection();
            }
        };

        const handleSelectionChange = () => {
            if (orderManagementStore.table.shiftPressed) {
                clearOrderTableSelection();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        window.addEventListener("blur", handleBlur);
        document.addEventListener("selectstart", handleSelectStart, true);
        document.addEventListener("mousedown", handleMouseDown, true);
        document.addEventListener("mouseup", handleShiftClickEnd, true);
        document.addEventListener("click", handleShiftClickEnd, true);
        document.addEventListener("selectionchange", handleSelectionChange);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            window.removeEventListener("blur", handleBlur);
            document.removeEventListener("selectstart", handleSelectStart, true);
            document.removeEventListener("mousedown", handleMouseDown, true);
            document.removeEventListener("mouseup", handleShiftClickEnd, true);
            document.removeEventListener("click", handleShiftClickEnd, true);
            document.removeEventListener("selectionchange", handleSelectionChange);
            restoreOrderTableTextSelect();
        };
    }, [isOver1000Orders]);

    useEffect(() => {
        orderManagementStore.table.updateLastSelectedIndex(null);
        orderManagementStore.table.updateLastUnSelectedIndex(null);
    }, [orderManagementStore.table.orders]);
};

export default useShiftClickOrderCheckbox;
